import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Spinner, Alert, Button, Badge } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { Page, PageHeader, Card, COLOR } from "../components/ui";
import { getAuthHeaders } from "../utils/auth";
import {
  API_URL_client_imports_status,
  API_URL_client_imports_preview,
  API_URL_client_imports_apply,
} from "../constants";

const SOURCES = [
  { code: "MX",    label: "México",  hint: "Solución Factible export" },
  { code: "USA",   label: "USA",     hint: "Client list export" },
  { code: "OTHER", label: "Otro",    hint: "Otro origen" },
];

// Colored status pill based on days since last import.
function StaleBadge({ days }) {
  if (days === null || days === undefined) {
    return <Badge color="secondary">Sin importar</Badge>;
  }
  let color = "success";
  if (days > 60) color = "danger";
  else if (days > 30) color = "warning";
  return <Badge color={color}>{days === 0 ? "Hoy" : `Hace ${days} día${days === 1 ? "" : "s"}`}</Badge>;
}

export default function ImportarClientes() {
  const [statusRows, setStatusRows] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [selectedSource, setSelectedSource] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState("");

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(API_URL_client_imports_status, { headers: getAuthHeaders() });
      setStatusRows(res.data || []);
    } catch (e) {
      setErr("No se pudo cargar el historial de importaciones.");
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const startImport = (source) => {
    setSelectedSource(source);
    setFile(null);
    setPreview(null);
    setErr("");
  };

  const cancelImport = () => {
    setSelectedSource(null);
    setFile(null);
    setPreview(null);
    setErr("");
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setErr("");
    setPreviewing(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const res = await axios.post(API_URL_client_imports_preview, form, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
    } catch (e) {
      setErr(e.response?.data?.error || "No se pudo procesar el archivo.");
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    if (!file || !selectedSource) return;
    setApplying(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("source", selectedSource);
      const res = await axios.post(API_URL_client_imports_apply, form, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`${res.data.rows_new} clientes nuevos · ${res.data.rows_existing} ya existían`);
      cancelImport();
      loadStatus();
    } catch (e) {
      setErr(e.response?.data?.error || "No se pudo aplicar la importación.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title="Importar Clientes"
        subtitle="Actualiza el catálogo de clientes desde los exports de tus sistemas. Cada origen se guarda por separado."
      />

      {err && <Alert color="danger">{err}</Alert>}

      {!selectedSource && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          {SOURCES.map((s) => {
            const row = statusRows.find((r) => r.source === s.code) || { last_imported_at: null, days_since: null };
            return (
              <Card key={s.code}>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: "1.05rem", fontWeight: 600, color: COLOR.ink }}>{s.label}</div>
                      <div style={{ fontSize: "0.75rem", color: COLOR.muted, marginTop: 2 }}>{s.hint}</div>
                    </div>
                    <StaleBadge days={row.days_since} />
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: COLOR.muted }}>
                    {row.last_imported_at ? (
                      <>
                        Última: {new Date(row.last_imported_at).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" })}
                        {" · "}
                        <span>+{row.rows_new} nuevos</span>
                      </>
                    ) : (
                      "Nunca se ha importado desde esta origen."
                    )}
                  </div>
                  <Button color="primary" size="sm" onClick={() => startImport(s.code)}>
                    Importar {s.label}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {selectedSource && (
        <Card
          title={`Importar · ${SOURCES.find((s) => s.code === selectedSource)?.label}`}
          headerRight={
            <Button color="secondary" size="sm" onClick={cancelImport}>Cancelar</Button>
          }
        >
          {!preview && !previewing && (
            <div>
              <p style={{ color: COLOR.muted, marginBottom: 12 }}>
                Selecciona el archivo Excel exportado de tu sistema. Se buscarán columnas
                <em> Nombre</em>, <em>Cliente</em>, <em>Empresa</em> o similares en cada hoja.
              </p>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFile}
                style={{ padding: 8, border: `1px dashed ${COLOR.rule}`, borderRadius: 8, width: "100%" }}
              />
            </div>
          )}

          {previewing && (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Spinner size="sm" /> <span style={{ marginLeft: 8, color: COLOR.muted }}>Leyendo archivo…</span>
            </div>
          )}

          {preview && !previewing && (
            <div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                <div style={{ padding: "10px 14px", background: COLOR.subtle, border: `1px solid ${COLOR.rule}`, borderRadius: 8, minWidth: 140 }}>
                  <div style={{ fontSize: "0.7rem", color: COLOR.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Total en archivo</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: COLOR.ink }}>{preview.total_names}</div>
                </div>
                <div style={{ padding: "10px 14px", background: "#dcfce7", border: "1px solid #166534", borderRadius: 8, minWidth: 140 }}>
                  <div style={{ fontSize: "0.7rem", color: "#166534", textTransform: "uppercase", letterSpacing: "0.04em" }}>Nuevos</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#166534" }}>{preview.new_names?.length ?? 0}</div>
                </div>
                <div style={{ padding: "10px 14px", background: "#f3f4f6", border: `1px solid ${COLOR.rule}`, borderRadius: 8, minWidth: 140 }}>
                  <div style={{ fontSize: "0.7rem", color: COLOR.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Ya existentes</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: COLOR.muted }}>{preview.existing_names?.length ?? 0}</div>
                </div>
              </div>

              {(preview.new_names_preview?.length ?? 0) > 0 && (
                <details style={{ marginBottom: 12 }} open>
                  <summary style={{ cursor: "pointer", fontWeight: 600, color: "#166534" }}>
                    Ver clientes nuevos ({preview.new_names.length})
                  </summary>
                  <ul style={{ maxHeight: 200, overflowY: "auto", background: "#f9fafb", border: `1px solid ${COLOR.rule}`, borderRadius: 6, padding: "8px 24px", marginTop: 6 }}>
                    {preview.new_names_preview.map((n, i) => <li key={i} style={{ fontSize: "0.85rem" }}>{n}</li>)}
                    {preview.new_names.length > preview.new_names_preview.length && (
                      <li style={{ fontSize: "0.75rem", color: COLOR.muted }}>… y {preview.new_names.length - preview.new_names_preview.length} más</li>
                    )}
                  </ul>
                </details>
              )}

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: `1px solid ${COLOR.rule}`, paddingTop: 12 }}>
                <Button color="secondary" onClick={cancelImport} disabled={applying}>Cancelar</Button>
                <Button color="primary" onClick={handleApply} disabled={applying || (preview.new_names?.length ?? 0) === 0}>
                  {applying ? <><Spinner size="sm" /> Aplicando…</> : `Importar ${preview.new_names?.length ?? 0} clientes nuevos`}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {loadingStatus && !statusRows.length && (
        <div style={{ textAlign: "center", padding: "32px 0" }}><Spinner size="sm" /></div>
      )}

      <ToastContainer position="bottom-right" autoClose={4000} />
    </Page>
  );
}
